import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAnuncio, deleteAnuncio, getTablonComentarios, createTablonComentario } from '../services/api';
import { useToast } from '../components/Toast';
import Votacion from '../components/Votacion';
import ConfirmModal from '../components/ConfirmModal';
import './AnuncioDetalle.css';

function AnuncioDetalle({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [anuncio, setAnuncio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comentarios, setComentarios] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  useEffect(() => {
    cargarAnuncio();
    cargarComentarios();
  }, [id]);

  const cargarAnuncio = async () => {
    try {
      const response = await getAnuncio(id);
      setAnuncio(response.data);
    } catch (error) {
      console.error('Error:', error);
      navigate('/tablon');
    } finally {
      setLoading(false);
    }
  };

  const cargarComentarios = async () => {
    try {
      const response = await getTablonComentarios(id);
      setComentarios(response.data);
    } catch (error) {
      console.error('Error al cargar comentarios:', error);
    }
  };

  const handleEnviarComentario = async (e) => {
    e.preventDefault();
    if (!nuevoComentario.trim()) return;

    setEnviandoComentario(true);
    try {
      await createTablonComentario(id, nuevoComentario);
      setNuevoComentario('');
      cargarComentarios();
      toast.success('Comentario añadido');
    } catch (error) {
      toast.error('Error al añadir comentario');
    } finally {
      setEnviandoComentario(false);
    }
  };

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ isOpen: true, title, message, onConfirm });
  };
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  const handleEliminar = () => {
    openConfirm(
      'Eliminar anuncio',
      '¿Estás seguro de que quieres eliminar este anuncio? Esta acción no se puede deshacer.',
      async () => {
        closeConfirm();
        try {
          await deleteAnuncio(id);
          navigate('/tablon');
        } catch (error) {
          toast.error('Error al eliminar anuncio');
        }
      }
    );
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      nota: '📌',
      comunicado: '📢',
      convocatoria: '📅',
      votacion: '📊'
    };
    return icons[tipo] || '📄';
  };

  const getTipoColor = (tipo) => {
    const colors = {
      nota: '#3b82f6',
      comunicado: '#10b981',
      convocatoria: '#f59e0b',
      votacion: '#ef4444'
    };
    return colors[tipo] || '#6b7280';
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  if (!anuncio) {
    return null;
  }

  const puedeGestionar = ['administrador', 'presidente'].includes(user.rol);

  return (
    <div className="anuncio-detalle-page">
      <button 
        onClick={() => navigate('/tablon')} 
        className="btn btn-outline btn-sm anuncio-detalle-back"
      >
        ← Volver al Tablón
      </button>

      <div className="card anuncio-detalle-card" style={{ borderLeftColor: getTipoColor(anuncio.tipo) }}>
        <div className="anuncio-detalle-header">
          <div className="anuncio-detalle-title-wrap">
            <span className="anuncio-detalle-icon">{getTipoIcon(anuncio.tipo)}</span>
            <div>
              <h1 className="anuncio-detalle-title">
                {anuncio.titulo}
              </h1>
              <div className="anuncio-detalle-tags">
                <span 
                  className="badge anuncio-detalle-tipo"
                  style={{ backgroundColor: `${getTipoColor(anuncio.tipo)}20`, color: getTipoColor(anuncio.tipo) }}
                >
                  {anuncio.tipo}
                </span>
              </div>
            </div>
          </div>
          {puedeGestionar && (
            <button 
              onClick={handleEliminar} 
              className="btn btn-sm btn-danger"
            >
              Eliminar
            </button>
          )}
        </div>

        <div className="anuncio-detalle-meta-box">
          <div className="anuncio-detalle-meta">
            <p>
              <strong>Publicado por:</strong> {anuncio.autor_nombre} ({anuncio.autor_rol})
            </p>
            <p>
              <strong>Fecha:</strong> {new Date(anuncio.fecha_creacion).toLocaleString('es-ES')}
            </p>
            {anuncio.comunidad_nombre && (
              <p>
                <strong>Comunidad:</strong> {anuncio.comunidad_nombre}
              </p>
            )}
          </div>
        </div>

        <div className="anuncio-detalle-content">
          <h3 className="anuncio-detalle-section-title">
            Contenido
          </h3>
          <div className="anuncio-detalle-text">
            {anuncio.contenido}
          </div>
        </div>

        {anuncio.archivo && (
          <div className="anuncio-detalle-attachment">
            <h3 className="anuncio-detalle-section-title">
              📎 Archivo Adjunto
            </h3>
            <a
              href={anuncio.archivo}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              📥 Descargar Archivo
            </a>
          </div>
        )}
      </div>

      {/* Votación/Encuesta - solo si el tipo es votacion */}
      {anuncio.tipo === 'votacion' && <Votacion anuncioId={id} puedeGestionar={puedeGestionar} />}

      {/* Sección de Comentarios */}
      <div className="card anuncio-comentarios-card">
        <h3 className="anuncio-comentarios-title">
          💬 Comentarios ({comentarios.length})
        </h3>

        {/* Formulario para nuevo comentario */}
        <form onSubmit={handleEnviarComentario} className="anuncio-comentarios-form">
          <div className="form-group">
            <textarea
              className="form-control"
              placeholder="Escribe un comentario..."
              value={nuevoComentario}
              onChange={(e) => setNuevoComentario(e.target.value)}
              rows="3"
              required
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={enviandoComentario || !nuevoComentario.trim()}
          >
            {enviandoComentario ? 'Enviando...' : 'Enviar Comentario'}
          </button>
        </form>

        {/* Lista de comentarios */}
        <div className="anuncio-comentarios-list">
          {comentarios.length === 0 ? (
            <p className="anuncio-comentarios-empty">
              No hay comentarios todavía. ¡Sé el primero en comentar!
            </p>
          ) : (
            comentarios.map((com) => (
              <div
                key={com.id}
                className="anuncio-comentario-item"
              >
                <div className="anuncio-comentario-header">
                  <div>
                    <strong className="anuncio-comentario-author">{com.usuario_nombre}</strong>
                    <span
                      className="badge anuncio-comentario-role"
                    >
                      {com.usuario_rol}
                    </span>
                  </div>
                  <span className="anuncio-comentario-time">
                    {new Date(com.fecha_creacion).toLocaleString('es-ES')}
                  </span>
                </div>
                <p className="anuncio-comentario-text">
                  {com.comentario}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        confirmLabel="Eliminar"
        variant="danger"
      />
    </div>
  );
}

export default AnuncioDetalle;
